import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'autopod-pwa-dismissed';
const DISMISS_DURATION_DAYS = 7; // Don't show again for 7 days after dismissal

// Detect iOS Safari
function isIOSSafari(): boolean {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      // iPad on iOS 13+ reports as MacIntel
      (navigator.platform === 'MacIntel' && 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 1);
    const isWebkit = /WebKit/.test(ua);
    const isChrome = /CriOS/.test(ua);
    const isFirefox = /FxiOS/.test(ua);

    // iOS Safari = iOS + WebKit but not Chrome or Firefox
    return isIOS && isWebkit && !isChrome && !isFirefox;
  } catch {
    return false;
  }
}

// Detect Android Chrome
function isAndroidChrome(): boolean {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || '';
    return /Android/.test(ua) && /Chrome/.test(ua);
  } catch {
    return false;
  }
}

export interface UsePWAInstallReturn {
  /** Whether the install prompt can be shown (native or manual) */
  canInstall: boolean;
  /** Whether the app is already installed (standalone mode) */
  isInstalled: boolean;
  /** Whether the prompt is currently visible */
  isPromptVisible: boolean;
  /** Whether this is iOS (requires manual install) */
  isIOS: boolean;
  /** Show the install prompt */
  showPrompt: () => void;
  /** Hide the prompt (user dismissed) */
  dismissPrompt: () => void;
  /** Trigger the native install dialog (Android only) */
  install: () => Promise<boolean>;
}

export function usePWAInstall(stationSlug?: string | null): UsePWAInstallReturn {
  const dismissedKey = stationSlug
    ? `${DISMISSED_KEY}-${stationSlug}`
    : DISMISSED_KEY;

  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const [isIOS] = useState(() => isIOSSafari());
  const [isAndroid] = useState(() => isAndroidChrome());

  // Check if already installed (standalone mode)
  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-expect-error - iOS Safari specific
      window.navigator.standalone === true;

    setIsInstalled(isStandalone);

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = (e: MediaQueryListEvent) => {
      setIsInstalled(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Capture the beforeinstallprompt event (Android/Chrome)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome's default install prompt
      e.preventDefault();
      // Save the event for later use
      setInstallPrompt(e as BeforeInstallPromptEvent);

      // Check if user previously dismissed
      try {
        const dismissed = localStorage.getItem(dismissedKey);
        if (dismissed) {
          const dismissedTime = parseInt(dismissed, 10);
          const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
          if (daysSinceDismissed < DISMISS_DURATION_DAYS) {
            return; // Don't show prompt yet
          }
          // Enough time has passed, clear the dismissal
          localStorage.removeItem(dismissedKey);
        }
      } catch { /* sandboxed iframe */ }

      // Show our custom prompt after a short delay
      setTimeout(() => {
        setIsPromptVisible(true);
      }, 3000); // Wait 3 seconds before showing
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Handle successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      setIsPromptVisible(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [dismissedKey]);

  // Show iOS install prompt (no beforeinstallprompt event on iOS)
  useEffect(() => {
    if (!isIOS || isInstalled) return;

    // Check if user previously dismissed
    try {
      const dismissed = localStorage.getItem(dismissedKey);
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < DISMISS_DURATION_DAYS) {
          return; // Don't show prompt yet
        }
        localStorage.removeItem(dismissedKey);
      }
    } catch { /* sandboxed iframe */ }

    // Show iOS install instructions after a delay
    const timer = setTimeout(() => {
      setIsPromptVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [isIOS, isInstalled, dismissedKey]);

  const showPrompt = useCallback(() => {
    if (installPrompt || isIOS) {
      setIsPromptVisible(true);
    }
  }, [installPrompt, isIOS]);

  const dismissPrompt = useCallback(() => {
    setIsPromptVisible(false);
    // Remember dismissal
    try { localStorage.setItem(dismissedKey, Date.now().toString()); } catch { /* sandboxed iframe */ }
  }, [dismissedKey]);

  const install = useCallback(async (): Promise<boolean> => {
    if (!installPrompt) return false;

    // Show the native install prompt
    await installPrompt.prompt();

    // Wait for user choice
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
      setIsPromptVisible(false);
      return true;
    }

    return false;
  }, [installPrompt]);

  // Can install if: has native prompt OR is iOS Safari (manual install)
  const canInstall = (!!installPrompt || isIOS || isAndroid) && !isInstalled;

  return {
    canInstall,
    isInstalled,
    isPromptVisible,
    isIOS,
    showPrompt,
    dismissPrompt,
    install,
  };
}
